import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Box, Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';
import type { TransitionProps } from '@mui/material/transitions';
import _, { cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { MAX_FILE_SIZE_20GB } from '@/constants';
import { useProgress } from '@/contexts/ProgressProvider';

import CountDownSpinner from '../../../components/fuel-spinner/CountDownSpinner';
import MetadataContent from '../../../components/MetadataContent';
import SimplePlayer from '../../../components/simple-player/SimplePlayer';
import { Permission } from '../../../enums';
import type { Item } from '../../../models';
import { CustomAttributes } from '../../../models/customAttributes';
import { FuelPortalService } from '../../../services';
import { useAppDispatch } from '../../../store';
import { AssetForm } from '../../../store/forms';
import generateAssetFormHashCode from '../../../store/forms/Asset-Editor/generateAssetFormHashCode';
import { SchemaType } from '../../../store/forms/Asset-Editor/SchemaTypeOptions';
import { applicationDataSelector } from '../../../store/slices/application-data';
import { assetEditorSelector, CreatAssetForm, UpdateAssetForm } from '../../../store/slices/asset-editor';
import { fuelUserAccessSliceSelector } from '../../../store/slices/fuelUserAccess';
import { batchUpload, SaveAssetJobsSelector, startJob } from '../../../store/slices/saveAssetJobsState/SaveAssetJobsState';
import { toSaveAssetRequest } from '../../../store/slices/saveAssetJobsState/toSaveAssetRequest';
import useAppUtils from '../../../utils/appUtils';
import clientUtils from '../../../utils/clientUtils';
import itemUtils from '../../../utils/itemUtils';
import AssetFieldsWidget from '../asset-fields-widget/asset-fields-widget';
import { getCommonSchemaType } from '../getSchemaTypeFromExtension';
import IngestWidget from '../ingest-widget';
import { validateAssetForm } from '../validateAssetForm';
import { validateFileExtension } from '../validateFileExtension';
import AssetDetailsActions from './AssetDetailsActions';
import AssetDialogHeader from './AssetDialogHeader';
import IngestFileListDialog from './IngestFileListDialog';

interface IPros {
  open: boolean;
  onClose: () => void;
  isNew?: boolean;
  processAssetsReturnedFromSaving?: ((newAssets: Array<Item>) => void) | null;
}

const AssetDetailsModal = ({ open, onClose, processAssetsReturnedFromSaving = null, isNew = false }: IPros) => {
  const { assetForm } = useSelector(assetEditorSelector);
  const { jobs } = useSelector(SaveAssetJobsSelector);
  const [currentTime, setCurrentTime] = useState<Number>(0);
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const { ouData } = useSelector(applicationDataSelector);
  const { vnConfirm, alert } = useAppUtils();
  const dispatch = useAppDispatch();
  const [isGeneratingClipDataByAI, setIsGeneratingClipDataByAI] = useState(false);
  const [isImageFullScreen, setIsImageFullScreen] = useState(false);
  const [rightOpen, setRightOpen] = React.useState(false);
  const [tagState, setTagState] = useState(CustomAttributes.Create());
  const [isEditingDisabled, setIsEditingDisabled] = useState(false);
  const [openIngestFileDialg, setOpenIngestFileDialg] = useState(false);
  const { loading, setProgress } = useProgress();

  const tagStateRef = useRef(CustomAttributes.Create());

  const assetFormValues = useForm<AssetForm>({
    defaultValues: useMemo(() => {
      // console.log('assetForm', assetForm)
      return assetForm;
    }, [assetForm]),
  });
  const { watch } = assetFormValues;

  const schemaType = watch('schemaType');
  const handleClose = () => {
    onEditAssetCancel(assetFormValues.getValues());
  };
  const onSetThumbnail = () => {
    if (currentTime > 0) {
      const form: AssetForm = cloneDeep(assetForm);
      if (form && form.originalAssetData && form.originalAssetData.data) form.originalAssetData.data.thumbnailDelay = currentTime;
      // setThumbnail(formObject);
      if (form && form.originalAssetData && form.originalAssetData.data && form.originalAssetData.data.thumbnailDelay) {
        const payload = toSaveAssetRequest(form, ouData.schemas, ouData.categories, fuelUserAccessData, ouData);
        setProgress(true, 'Saving asset...');
        FuelPortalService.updateAsset(payload).then((newAsset) => {
          if (processAssetsReturnedFromSaving) processAssetsReturnedFromSaving([newAsset]);
          FuelPortalService.GenerateThumbnailRequestForAsset(newAsset).then((res) => {
            if (res) {
              setProgress(false);
              alert('Operation Successful! It may take a few minutes for the thumbnail on the clip to be updated.');
            }
          });
        });
      }
    }
  };

  const onUploadThumbnail = async (file: File) => {
    const allowedFiles = ['.JPEG', '.jpeg', '.JPG', '.jpg', '.PNG', '.png', '.GIF', '.gif'];
    const regex = new RegExp(`([\\W\\S])+(${allowedFiles.join('|')})$`);
    const message = allowedFiles.join(', ');
    const bodyText = `Please upload files having extensions: ${message} only.`;
    if (file && !regex.test(file && file.name.toLowerCase())) {
      alert(bodyText, 'error');
      return;
    }
    const frozenForm: AssetForm = cloneDeep(assetForm);
    frozenForm.ingestFile = file;
    dispatch(CreatAssetForm(frozenForm));
    await dispatch(startJob({ form: frozenForm, jobType: 'Thumbnail' }));
  };

  const onPlayerCurrentTime = (currentTime: Number) => {
    // this.currentTime = currentTime;
    // console.log('currentTime', currentTime);
    setCurrentTime(currentTime);
  };

  const onAnalyzeWithAI = async () => {
    const assetForm = assetFormValues.getValues();
    // console.log('onTitleClick', assetForm);
    if (assetForm && assetForm.originalAssetData && assetForm.originalAssetData.id) {
      const clip = assetForm.originalAssetData;
      setIsGeneratingClipDataByAI(true);
      try {
        const response = await FuelPortalService.autoFillClip(clip);
        if (response && response.updatedClip) {
          const assetForm = await AssetForm.createFromData(response.updatedClip, permissions, ouData);
          dispatch(UpdateAssetForm(assetForm));
          setTimeout(() => {
            setIsGeneratingClipDataByAI(false);
          }, 1000 * 15);
        }
      } catch (e) {
        setIsGeneratingClipDataByAI(false);
        alert('Could not auto-generate metadata for the clip!', 'warning');
        // console.error('Could not auto-generate metadata for the clip!', e);
      }
    }
  };

  const onEditAssetSave = async (form: AssetForm) => {
    // console.log('onEditAssetSave', form);
    if (loading) {
      return;
    }
    const errors = validateAssetForm(form);
    form.errors = errors;
    dispatch(CreatAssetForm(form));
    // console.log('form--', form);

    if (isNew) {
      const totalFiles = jobs.length + form.ingestFiles.length;
      if (totalFiles > 100) {
        alert('You can upload only 100 assets at once.', 'warning');
        return;
      }
    }

    if (errors.hasAny()) {
      alert('Fix the errors below to continue.', 'error');
      return;
    }

    if (isNew) {
      const frozenForm = cloneDeep(form);
      // console.log('frozenForm', frozenForm);
      if (form.ingestFiles.length === 1) {
        frozenForm.ingestFile = form.ingestFiles[0];
        resetForm();
        await dispatch(startJob({ form: frozenForm, jobType: '' }));
      } else {
        const files = cloneDeep(frozenForm.ingestFiles);
        const newAssetForms: AssetForm[] = [];
        for (let i = 0; i < files.length; i++) {
          newAssetForms.push(getAssetForm(frozenForm, files[i]));
        }
        resetForm();
        const response = await dispatch(batchUpload({ forms: newAssetForms, jobType: 'BatchUpload' }));
        if (response && response.payload) {
          const assetsSavedSuccessfully = response.payload.filter((r) => r.status === 'fulfilled');
          const errorCount = response.payload.filter((r) => r.status !== 'fulfilled');
          if (errorCount > 0) {
            alert(
              `Not all assets created - ${assetsSavedSuccessfully.length} created successfully, ${errorCount} failed.`,
              'error'
            );
          } else {
            alert(`All ${assetsSavedSuccessfully.length} Assets created successfully.`);
          }
        }
      }
    } else {
      const payload = toSaveAssetRequest(form, ouData.schemas, ouData.categories, fuelUserAccessData, ouData);
      setProgress(true, 'Saving asset...');
      try {
        const newAsset = await FuelPortalService.updateAsset(payload);
        if (newAsset) {
          if (processAssetsReturnedFromSaving) processAssetsReturnedFromSaving([newAsset]);
          alert('Asset successfully saved.');
          setProgress(false);
          onClose();
        }
      } catch (ex) {
        alert('Asset successfully saved.');
        setProgress(false);
        console.error('Unable to save asset ', ex);
      }
    }
  };

  const onEditAssetCancel = async (form: AssetForm) => {
    const hashCode = generateAssetFormHashCode(form);
    if (!isNew && hashCode !== assetForm.initialHashCode) {
      const isOk = await vnConfirm('Are you sure you want to cancel?  Any unsaved changes will be lost.');
      if (isOk) {
        onClose();
      }
    } else {
      if (isNew) {
        resetForm();
        alert('Form reset');
      }
      onClose();
    }
  };

  const onInputChange = (event) => {
    // console.log('onInputChange', event);
    if (event.key === ',' || event.key === '\\') {
      event.preventDefault();
      alert('Commas and backslashes not allowed in tag names.', 'warning');
      return;
    }
    const myState = _.cloneDeep(tagStateRef.current);
    myState[event.target.name] = event.target.value;
    tagStateRef.current = myState;
    setTagState(myState);
  };
  const onSingleTagFieldBlur = (event) => {
    // console.log('onCustomTagFieldBlur2', event);
    const existingTags: string[] = assetFormValues.getValues('tags');
    const newTags = itemUtils.onSingleTagFieldBlur(event, existingTags);
    assetFormValues.setValue('tags', newTags, { shouldValidate: true });
    refreshLongFormFields(newTags);
  };
  const onMultipleTagFieldBlur = (event) => {
    const existingTags: string[] = assetFormValues.getValues('tags');
    const newTags = itemUtils.onMultipleTagFieldBlur(event, existingTags);
    // console.log('onCustomTagFieldBlur newTags', newTags);
    assetFormValues.setValue('tags', newTags, { shouldValidate: true });
    refreshLongFormFields(newTags);
  };
  const refreshLongFormFields = (tags) => {
    const myState = itemUtils.extractCustomAttributesFromTags(tags, tagStateRef.current);
    // console.log('refreshLongFormFields', tags);
    tagStateRef.current = myState;
    setTagState(myState);
  };
  const populateLongFormFields = (assetForm) => {
    // console.log('populateLongFormFields', assetForm);
    refreshLongFormFields(assetForm.tags);
  };
  const init = async () => {
    // console.log('mode', isNew);
    if (permissions && permissions.has(Permission.Ingest)) {
      setIsEditingDisabled(!permissions.has(Permission.EditAsset));
      if (isNew) {
        resetForm();
      }
      // resetForm();
      // setStage(UIStage.Initialized);
    }
  };

  const resetForm = () => {
    if (ouData) {
      const form = AssetForm.create(permissions, ouData);
      dispatch(CreatAssetForm(form));
      setIsEditingDisabled(!permissions.has(Permission.EditAsset));
      assetFormValues.setValue('isBatchUploadEnable', false);
      assetFormValues.reset(form);
    }
  };

  useEffect(() => {
    // console.log(assetFormValues);
    if (assetForm) {
      populateLongFormFields(assetForm);
      setRightOpen(assetForm.isLongFormContentEnabled);
      assetFormValues.reset(assetForm);
    }
    return () => {
      // cleanup
    };
  }, [assetForm]);

  const onFilesChange = (files: File[]) => {
    // console.log('file change', files);/
    // const fileSize = file.size;
    // console.log('onFileChange asset-editor.vue', { fileSize });
    const currentFiles = assetFormValues.getValues('ingestFiles');
    const ingestFiles = [...currentFiles];
    // old limit without multipart upload
    // let maxFileSize = MAX_FILE_SIZE_5GB;
    const maxFileSize = MAX_FILE_SIZE_20GB;

    for (let i = 0; i < files.length; i++) {
      const fileSize = files[i].size;
      if (fileSize >= maxFileSize.fileSize) {
        alert(`Maximum allowed file size is ${maxFileSize.name}`, 'error');
        return;
      }
      ingestFiles.push(files[i]);
    }

    if (ingestFiles.length === 0) return;

    const isBatchUpload = ingestFiles.length > 1;
    assetFormValues.setValue('ingestFiles', ingestFiles);
    assetFormValues.setValue('isBatchUploadEnable', isBatchUpload);

    // Schema logic
    const wasEmptyBefore = currentFiles.length === 0;
    const newFilesSchemaType = getCommonSchemaType(ingestFiles);
    if (!newFilesSchemaType) {
      assetFormValues.setValue('ingestFiles', currentFiles);
      onFormChanged();
      alert('Please select files with same file type.', 'error');
      return;
    }

    const isSingleFile = ingestFiles.length === 1;

    if (wasEmptyBefore && newFilesSchemaType) {
      assetFormValues.setValue('schemaType', newFilesSchemaType);
    } else if (isSingleFile) {
      const schemaType = newFilesSchemaType || SchemaType.Video;
      assetFormValues.setValue('schemaType', schemaType);
    }

    //  Title / Meta
    if (isBatchUpload) {
      assetFormValues.setValue('title', '');
      assetFormValues.setValue('synopsis', '');
    } else {
      const firstFile = ingestFiles[0];
      assetFormValues.setValue('title', clientUtils.getAssetTitle(firstFile.name));
      assetFormValues.setValue('originalFileName', firstFile.name);
    }
    onFormChanged();
  };
  const getAssetForm = (form, file) => {
    const newAssetForm: AssetForm = cloneDeep(form);
    const fileName = clientUtils.removeExtension(file.name);
    newAssetForm.title = fileName;
    newAssetForm.originalFileName = file.name;
    newAssetForm.ingestFile = file;
    return newAssetForm;
  };
  const deleteFile = (file, key) => {
    const newFileList = cloneDeep(assetForm.ingestFiles);
    newFileList.splice(key, 1);
    assetFormValues.setValue('ingestFiles', newFileList);
    if (newFileList.length === 0) {
      setOpenIngestFileDialg(false);
    }
    if (newFileList.length === 1) {
      assetFormValues.setValue('isBatchUploadEnable', false);
    }
    if (newFileList.length > 1) {
      setOpenIngestFileDialg(true);
    }
    onFormChanged();
  };

  const onFormChanged = () => {
    // console.log('onFormChanged', { form: assetFormValues.getValues() });
    const form: AssetForm = cloneDeep(assetFormValues.getValues());
    const errors = validateAssetForm(form);
    const files: File[] = [];
    for (let i = 0; i < form.ingestFiles.length; i++) {
      const error = validateFileExtension(form.schemaType, form.ingestFiles[i].name);
      if (!error) {
        files.push(form.ingestFiles[i]);
      }
    }
    form.ingestFiles = files;
    form.errors = errors;
    if (files.length <= 1) {
      form.isBatchUploadEnable = false;
    }
    assetFormValues.reset(form);
    dispatch(CreatAssetForm(form));
  };
  const onTitleClick = () => {
    if (!isNew) {
      const assetForm = assetFormValues.getValues();
      // console.log('onTitleClick', assetForm);
      const clipId = assetForm.originalAssetData.id;
      clientUtils.copyToClipboard(clipId);
    }
  };

  const toggleEnlargedThumbnail = () => {
    // console.log('toggleEnlargedThumbnail');
    setIsImageFullScreen(true);
    // const assetForm = assetFormValues.getValues();
    // const previewUri = assetForm.originalAssetData?.data?.videoProxy?.previewUri;
    // if (previewUri) {
    //   window.open(previewUri, '_blank');
    // }
  };

  // Fullscreen Image transition
  const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

  const expandRightSide = (val) => {
    // console.log('val', val);
    setRightOpen(!rightOpen);
    assetFormValues.setValue('isLongFormContentEnabled', val);
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <div>
      <Dialog open={open} fullWidth maxWidth={rightOpen ? 'lg' : 'md'} className="asset-details-modal">
        <Grid container>
          <Grid item xs={rightOpen ? 8 : 12}>
            <AssetDialogHeader
              title={isNew ? 'Upload Assets' : 'Edit Asset'}
              onClose={onClose}
              onTitleClick={onTitleClick}
              rightOpen={rightOpen}
              expandRightSide={expandRightSide}
              showRightExpandIcon={schemaType !== SchemaType.Graphics}
            />
            <DialogContent
              dividers
              sx={{
                height:
                  assetForm.schemaType === SchemaType.Image || assetForm.schemaType === SchemaType.Graphics ? '58vh' : '65vh',
              }}
            >
              {!isNew && isGeneratingClipDataByAI && <CountDownSpinner />}
              {!isNew &&
                assetForm &&
                (assetForm.schemaType === SchemaType.Video || assetForm.schemaType === SchemaType.Graphics) &&
                assetForm.assetSrc && (
                  <div className="edit-asset-player">
                    <SimplePlayer
                      srcUrl={assetForm.assetSrc}
                      onTimeUpdate={onPlayerCurrentTime}
                      markers={assetForm.markersForPlayer}
                    />
                  </div>
                )}
              {isNew && (
                <div>
                  <IngestWidget
                    filesChange={onFilesChange}
                    assetForm={assetForm}
                    openDialog={() => setOpenIngestFileDialg(!openIngestFileDialg)}
                  />
                </div>
              )}
              {isNew && openIngestFileDialg && (
                <IngestFileListDialog
                  open={openIngestFileDialg}
                  onClose={() => setOpenIngestFileDialg(false)}
                  deleteFile={deleteFile}
                />
              )}
              {!isNew && assetForm && assetForm.schemaType === SchemaType.Image && (
                <div className="image-thumbnail-container">
                  <img
                    className="image-thumbnail"
                    src={assetForm.originalAssetData?.data?.thumbnail?.previewUri}
                    onClick={toggleEnlargedThumbnail}
                    title="CLICK TO ENLARGE"
                  />
                  <FullscreenIcon className="fullscreen-icon" fontSize="large" />
                </div>
              )}
              <AssetFieldsWidget
                assetFormValues={assetFormValues}
                assetForm={assetForm}
                rightOpen={rightOpen}
                expandRightSide={expandRightSide}
                onFormChanged={onFormChanged}
                isNew={isNew}
              />
            </DialogContent>
          </Grid>
          {rightOpen && (
            <Grid item xs={4} sx={{ borderLeft: '1px solid #ddd' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <Box>
                  {/* <IconButton aria-label="close" onClick={onClose}>
                        <CloseIcon />
                      </IconButton> */}
                </Box>
                <IconButton onClick={expandRightSide}>
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
              <DialogContent dividers sx={{ height: '65vh' }}>
                <MetadataContent
                  tagState={tagState}
                  isEditingDisabled={isEditingDisabled}
                  onInputChange={onInputChange}
                  onSingleTagFieldBlur={onSingleTagFieldBlur}
                  onMultipleTagFieldBlur={onMultipleTagFieldBlur}
                />
              </DialogContent>
            </Grid>
          )}
        </Grid>

        <DialogActions>
          <AssetDetailsActions
            assetFormValues={assetFormValues}
            assetForm={assetForm}
            onSaveClick={onEditAssetSave}
            onCancelClick={handleClose}
            setThumbnail={onSetThumbnail}
            uploadThumbnail={onUploadThumbnail}
            onAnalyzeWithAI={onAnalyzeWithAI}
            rightOpen={rightOpen}
            expandRightSide={expandRightSide}
            isNew={isNew}
          />
        </DialogActions>
      </Dialog>
      {isImageFullScreen && (
        <Dialog
          className="fullscreen-image-popup"
          fullScreen
          open
          onClose={() => setIsImageFullScreen(false)}
          TransitionComponent={Transition}
        >
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={() => setIsImageFullScreen(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <div className="image-thumbnail-container">
            <img className="image-thumbnail" src={assetForm.originalAssetData?.data?.thumbnail?.previewUri} />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default AssetDetailsModal;
